import { CircleNotchIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { GoogleSsoButton } from "@/components/google-sso-button";
import { StepBars } from "@/components/step-bars";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { apiErrorDetail, apiErrorStatus, useAuthProviders, useLogin, useMe } from "@/lib/api";
import { PROJECT_NAME } from "@/lib/brand-assets";
import { logger } from "@/lib/logger";
import { hasPasswordResetNotice } from "@/lib/login-notice";
import { retryAfterLabel } from "@/lib/retry-after";
import { homeForRole, isSafeRelativePath, safeNextForRole } from "@/lib/roles";
import { ssoErrorMessage } from "@/lib/sso-errors";

// Constant-length JSX expression keeps prettier output stable for any rendered name.

const SSO_ERROR_PARAM = "sso_error";
const NEXT_PARAM = "next";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();
  const { data: me } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const next = searchParams.get(NEXT_PARAM);
  // Read once on mount: the effect below strips the param from the URL, but the
  // message should stay on screen.
  const [ssoError] = useState(() => {
    const code = searchParams.get(SSO_ERROR_PARAM);
    return code ? ssoErrorMessage(code) : "";
  });
  // Read once on mount too: the effect below clears it from the history entry.
  const [passwordReset] = useState(() => hasPasswordResetNotice(location.state));
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Drop the one-shot signals, `?sso_error=` from the URL and the reset notice
  // from the history entry, so a refresh or a shared link doesn't replay them.
  // `?next=` stays.
  useEffect(() => {
    const ssoErrorCode = searchParams.get(SSO_ERROR_PARAM);
    if (ssoErrorCode === null && !hasPasswordResetNotice(location.state)) {
      return;
    }
    if (ssoErrorCode !== null) {
      logger.debug("[auth] Google SSO failed:", ssoErrorCode);
    }
    const remaining = new URLSearchParams(searchParams);
    remaining.delete(SSO_ERROR_PARAM);
    setSearchParams(remaining, { replace: true, state: null });
  }, [searchParams, setSearchParams, location.state]);

  if (me) {
    const target = safeNextForRole(next, me.role) ?? homeForRole(me.role);
    logger.debug("[auth] already signed in → redirecting to", target);
    return <Navigate to={target} replace />;
  }

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    loginMutation.mutate(
      { email, password, rememberMe },
      {
        onSuccess: (result) => {
          const role = result?.role ?? "client";
          const target = safeNextForRole(next, role) ?? homeForRole(role);
          logger.debug("[login] success → redirecting to", target);
          navigate(target);
        },
        onError: (err) => {
          const status = apiErrorStatus(err);
          const detail = apiErrorDetail(err);
          logger.debug("[login] failed:", status, detail);
          if (status === 403 && detail === "email_not_verified") {
            navigate(`/verify-email?email=${encodeURIComponent(email)}`);
            return;
          }
          setError(loginErrorMessage(err));
        },
      },
    );
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <section className="bg-primary text-primary-foreground relative hidden min-h-[640px] overflow-hidden rounded-md p-12 lg:flex lg:flex-col lg:justify-between">
          <div className="space-y-6">
            <p className="text-accent-on-dark t-eyebrow">Board of Innovation</p>
            <div className="max-w-xl space-y-5">
              <h1 className="t-display">
                Redesign work around{" "}
                <span className="text-accent-on-dark">the outcomes that matter.</span>
              </h1>
              <p className="text-primary-foreground/75 max-w-lg text-lg leading-7">
                From how the work runs today to where AI takes it, evidenced at every step.
              </p>
            </div>
          </div>
          <StepBars className="justify-start" />
        </section>
        <Card
          variant="box"
          className="w-full max-w-md justify-self-center px-0 py-0 lg:max-w-none lg:self-center"
        >
          <CardHeader className="space-y-6 px-8 pt-8 text-left">
            <p className="text-muted t-eyebrow">AI-first workflow redesign</p>
            <BrandLogo name={PROJECT_NAME} className="h-7 text-2xl" />
            <div className="space-y-2">
              <h2 className="t-title text-foreground">Sign in</h2>
              <p className="text-muted text-sm leading-6">
                BOI teams work in the cockpit; clients work in their own portal.
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 px-8 pb-8">
            {passwordReset && (
              <p role="status" className="text-success text-sm">
                Password updated. Sign in with your new password.
              </p>
            )}
            {ssoError && (
              <p role="alert" className="text-destructive text-sm">
                {ssoError}
              </p>
            )}
            <div className="space-y-4">
              <StaffSignIn next={next !== null && isSafeRelativePath(next) ? next : undefined} />
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="bg-border h-px flex-1" />
                <span className="text-muted t-eyebrow">or</span>
                <span className="bg-border h-px flex-1" />
              </div>
              <p className="text-muted text-center text-xs leading-5">
                Clients: sign in with the email and password from your invitation.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground t-eyebrow">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground t-eyebrow">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                  />
                  <label htmlFor="rememberMe" className="text-foreground text-sm">
                    Keep me signed in
                  </label>
                </div>
                <span className="text-muted t-eyebrow">Secure access</span>
              </div>
              {error && (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={loginMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                {loginMutation.isPending ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Enter workspace"
                )}
              </Button>
            </form>
            <p className="text-center text-sm">
              <Link to="/forgot-password" className="text-accent-text hover:underline">
                Forgot your password?
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface StaffSignInProps {
  /** A safe `?next=` to hand to the Google flow. */
  next: string | undefined;
}

/**
 * The Google button, shown only while the backend reports Google as a
 * provider it can start. A failed check reads as "not available": a button
 * that leads to a provider error is worse than no button.
 */
function StaffSignIn({ next }: StaffSignInProps) {
  const providersQuery = useAuthProviders();

  // A fixed height, so the password form doesn't jump when the check returns.
  return (
    <div className="flex min-h-18 flex-col justify-center">
      {providersQuery.isLoading ? (
        <div aria-hidden="true" className="bg-muted/10 h-11 w-full animate-pulse rounded-full" />
      ) : providersQuery.data?.providers.includes("google") ? (
        <GoogleSsoButton next={next} />
      ) : (
        <p role="status" className="text-muted text-center text-sm">
          BOI sign-in is temporarily unavailable.
        </p>
      )}
    </div>
  );
}

function loginErrorMessage(err: unknown): string {
  const status = apiErrorStatus(err);
  const detail = apiErrorDetail(err);
  if (status === 429) {
    const wait = retryAfterLabel(err) ?? "a few minutes";
    return `Too many sign-in attempts. Please wait ${wait} and try again.`;
  }
  if (status === 403 && detail === "account_disabled") {
    return "This account has been deactivated. Contact your BOI team.";
  }
  if (status === 403 && detail === "sso_required") {
    return "BOI accounts sign in with Google (button above).";
  }
  if (status === 401) {
    return "Invalid credentials.";
  }
  if (status === 502 || status === 0) {
    return "Sign-in is temporarily unavailable. Please try again.";
  }
  return "Sign-in failed. Please try again.";
}
